/**
 * One client, in full, and editable.
 *
 * The other half of the register. The list answers "who do we have"; this
 * answers "what do we know about them", and it is the only screen in the suite
 * where a client's details may be changed -- Practice is the sole writer of the
 * client register (SUITE-DECISIONS R8/T2), so Accounts and Payroll read what is
 * typed here and neither may write it back.
 *
 * THREE THINGS THIS PAGE DOES DELIBERATELY.
 *
 * 1. It PATCHes only what changed. Sending the whole form back would mean a
 *    validation error naming a field the person never touched, and it would
 *    overwrite a value somebody else edited between load and save with the
 *    stale copy this browser happens to be holding.
 *
 * 2. It shows `clientNumber` and `smartpracticeCustomerId` as facts, not as
 *    disabled inputs. A greyed-out box invites the question "why can't I type
 *    here"; a labelled value with a sentence under it answers it. Both are
 *    refused by the server and by the database regardless of what this page
 *    renders -- see clients-view.ts.
 *
 * 3. Blank means blank. An emptied text box sends null, not "", so a client
 *    whose VAT number was entered by mistake can have it removed rather than
 *    replaced with an empty string that every downstream `IS NOT NULL` would
 *    read as "registered".
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  api,
  type AuthState,
  type ClientDetail,
  type ClientGroupRow,
  type ClientChangeRow,
  type ClientPatch,
  type DuplicateMatch,
  type ObligationTypeInUse,
  type ProductReadiness,
  type VatCategoryRow,
} from '../api'
import { AppShell } from '../components/AppShell'
import { ClientTimeTab } from '../components/ClientTime'
import { ClientWork } from '../components/ClientWork'
import { ErrorState, LoadingState } from '../components/States'
import { LogTimeDialog } from '../components/TimeCapture'
import { vatTerm } from '../lib/vatTerms'

const ENTITY_TYPES = [
  ['individual', 'Individual'],
  ['company', 'Company'],
  ['cc', 'Close corporation'],
  ['trust', 'Trust'],
  ['partnership', 'Partnership'],
  ['deceased_estate', 'Deceased estate'],
  ['sole_proprietor', 'Sole proprietor'],
  ['npo', 'NPO'],
] as const

const STATUSES = [
  ['active', 'Active'],
  ['dormant', 'Dormant'],
  ['resigned', 'Resigned'],
] as const

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * The form's own state: every editable field as the string a box holds.
 *
 * The keys are a literal union, NOT `Record<string, string>`. Under this
 * project's `noUncheckedIndexedAccess: true`, an index signature makes every
 * read `string | undefined`, so `value={draft.shortName}` fails to typecheck
 * against an input expecting `string` -- sixteen times over. That is what
 * broke the first deploy, 2026-09-09.
 *
 * A mapped type over known literal keys is exempt: the compiler knows every
 * one of them is present. It also means a typo in a field name is a compile
 * error rather than a silently-undefined box.
 */
const FIELDS = [
  'shortName',
  'registeredName',
  'tradingName',
  'entityType',
  'status',
  'groupId',
  'registrationNumber',
  'idNumber',
  'incomeTaxNumber',
  'vatNumber',
  'payeNumber',
  'uifNumber',
  'coidaNumber',
  'financialYearEndMonth',
  'vatCategoryCode',
  'shelfNumber',
  // R13. `vatFilingPeriodStart` is NOT here any more: the bi-monthly cycle is
  // the VAT category's (A = 1, B = 2), the server derives it, and the page
  // shows it rather than asking for it -- one fact, typed once.
  'vatRegistered',
  'vatRegisteredFrom',
  'uifRefSars',
  'sdlNumber',
  'sicCode',
  'tradeClassification',
  'physUnit',
  'physComplex',
  'physStreetNo',
  'physStreetName',
  'physSuburb',
  'physCity',
  'physPostalCode',
  'physCountry',
  'postalSameAsPhysical',
  'postalLine1',
  'postalLine2',
  'postalLine3',
  'postalPostalCode',
] as const

type Field = (typeof FIELDS)[number]
type Draft = Record<Field, string>

const draftFrom = (c: ClientDetail): Draft => ({
  shortName: c.shortName,
  registeredName: c.registeredName ?? '',
  tradingName: c.tradingName ?? '',
  entityType: c.entityType ?? '',
  status: c.status,
  groupId: c.groupId ?? '',
  registrationNumber: c.registrationNumber ?? '',
  idNumber: c.idNumber ?? '',
  incomeTaxNumber: c.incomeTaxNumber ?? '',
  vatNumber: c.vatNumber ?? '',
  payeNumber: c.payeNumber ?? '',
  uifNumber: c.uifNumber ?? '',
  coidaNumber: c.coidaNumber ?? '',
  financialYearEndMonth: c.financialYearEndMonth === null ? '' : String(c.financialYearEndMonth),
  vatCategoryCode: c.vatCategoryCode ?? '',
  shelfNumber: c.shelfNumber === null ? '' : String(c.shelfNumber),
  vatRegistered: c.vatRegistered === null ? '' : String(c.vatRegistered),
  vatRegisteredFrom: c.vatRegisteredFrom === null ? '' : c.vatRegisteredFrom.slice(0, 10),
  uifRefSars: c.uifRefSars ?? '',
  sdlNumber: c.sdlNumber ?? '',
  sicCode: c.sicCode ?? '',
  tradeClassification: c.tradeClassification ?? '',
  physUnit: c.physUnit ?? '',
  physComplex: c.physComplex ?? '',
  physStreetNo: c.physStreetNo ?? '',
  physStreetName: c.physStreetName ?? '',
  physSuburb: c.physSuburb ?? '',
  physCity: c.physCity ?? '',
  physPostalCode: c.physPostalCode ?? '',
  physCountry: c.physCountry ?? '',
  postalSameAsPhysical: String(c.postalSameAsPhysical),
  postalLine1: c.postalLine1 ?? '',
  postalLine2: c.postalLine2 ?? '',
  postalLine3: c.postalLine3 ?? '',
  postalPostalCode: c.postalPostalCode ?? '',
})

/** Fields whose empty string means "no value", not "the empty string". */
const NUMERIC: ReadonlySet<Field> = new Set<Field>([
  'financialYearEndMonth',
  'shelfNumber',
])

/** Held as 'true' / 'false' / '' in the form; sent as a boolean or null. */
const BOOLEAN: ReadonlySet<Field> = new Set<Field>(['vatRegistered', 'postalSameAsPhysical'])

/**
 * Mirrors SENSITIVE_FIELDS in server/src/clients-view.ts: changing one of
 * these from a value it already had needs a reason, recorded with the change.
 * The server is the rule; this only decides when to ask.
 */
const SENSITIVE: ReadonlySet<Field> = new Set<Field>([
  'registrationNumber',
  'idNumber',
  'entityType',
  'status',
  'financialYearEndMonth',
  'vatRegistered',
  'vatRegisteredFrom',
  'vatNumber',
  'vatCategoryCode',
])

const FIELD_WORDS: Record<string, string> = {
  '(created)': 'Client created',
  contact: 'Contact',
  shortName: 'Short name',
  registeredName: 'Legal name',
  tradingName: 'Trading name',
  entityType: 'Entity type',
  status: 'Status',
  groupId: 'Group',
  registrationNumber: 'Registration number',
  idNumber: 'SA ID number',
  incomeTaxNumber: 'Income tax number',
  vatNumber: vatTerm('VAT_TERM_VAT_REGISTRATION_NUMBER'),
  vatRegistered: vatTerm('VAT_TERM_VAT_REGISTRATION_STATUS'),
  vatRegisteredFrom: vatTerm('VAT_TERM_VAT_REGISTRATION_DATE'),
  vatCategoryCode: vatTerm('VAT_TERM_VAT_TAX_PERIOD_FREQUENCY'),
  vatFilingPeriodStart: 'VAT cycle',
  financialYearEndMonth: 'Financial year-end',
  payeNumber: 'PAYE number',
  uifNumber: 'UIF number',
  uifRefSars: 'UIF payment reference',
  sdlNumber: 'SDL number',
  coidaNumber: 'COIDA number',
}

/** A stored audit value, in the words the form uses. */
function historyValue(field: string, value: string | null): string {
  if (value === null) return '—'
  if (field === 'vatRegistered') return value === 'true' ? vatTerm('VAT_TERM_REGISTERED_FOR_VAT') : vatTerm('VAT_TERM_NOT_REGISTERED_FOR_VAT')
  if (field === 'postalSameAsPhysical') return value === 'true' ? 'Same as registered' : 'Different'
  if (field === 'financialYearEndMonth') return MONTHS[Number(value) - 1] ?? value
  return value
}

const PRODUCT_NAMES: Record<string, string> = { accounts: 'EQ Accounts', payroll: 'EQ Payroll' }

/**
 * Just the changes, typed for the API.
 *
 * A field that is unchanged is absent, so the server never sees it and the
 * database never writes it -- see note 1 in the header.
 */
function changedFields(before: Draft, after: Draft): ClientPatch {
  const patch: Record<string, unknown> = {}
  // Iterating FIELDS rather than Object.keys keeps `key` a literal type, so
  // the indexing below is checked rather than widened back to `string`.
  for (const key of FIELDS) {
    if (after[key] === before[key]) continue
    const value = after[key].trim()
    if (BOOLEAN.has(key)) {
      patch[key] = value === '' ? null : value === 'true'
      continue
    }
    if (value === '') {
      // shortName has no blank form -- the server says so, in a sentence.
      patch[key] = key === 'shortName' ? '' : null
    } else if (NUMERIC.has(key)) {
      patch[key] = Number(value)
    } else {
      patch[key] = value
    }
  }
  return patch as ClientPatch
}

export function ClientDetailPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const { id = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'time' ? 'time' : 'details'
  /** Bumped by Log time, so the Time tab shows the new draft. */
  const [timeVersion, setTimeVersion] = useState(0)

  const [client, setClient] = useState<ClientDetail | null>(null)
  const [groups, setGroups] = useState<ClientGroupRow[]>([])
  const [vatCategories, setVatCategories] = useState<VatCategoryRow[]>([])
  const [saved, setSaved] = useState<Draft | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [reason, setReason] = useState('')
  const [changes, setChanges] = useState<ClientChangeRow[]>([])
  const [readiness, setReadiness] = useState<ProductReadiness[] | null>(null)
  const [readinessError, setReadinessError] = useState<string | null>(null)
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([])

  const adopt = useCallback((c: ClientDetail) => {
    setClient(c)
    const d = draftFrom(c)
    setSaved(d)
    setDraft(d)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    setClient(null)
    api
      .client(id)
      .then((c) => {
        if (!cancelled) adopt(c)
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e))
      })
    return () => {
      cancelled = true
    }
  }, [id, adopt])

  // The history and the product checklist follow every save, so they are
  // reloaded whenever the client itself is (re)adopted.
  useEffect(() => {
    if (client === null) return
    void api.clientChanges(client.id).then((r) => setChanges(r.changes)).catch(() => setChanges([]))
    api
      .clientReadiness(client.id)
      .then((r) => {
        setReadiness(r.products)
        setReadinessError(null)
      })
      .catch((e: unknown) => setReadinessError(e instanceof Error ? e.message : String(e)))
  }, [client])

  // The pickers are reference data and do not depend on which client is open.
  useEffect(() => {
    void api.clientGroups().then((r) => setGroups(r.groups)).catch(() => setGroups([]))
    void api.vatCategories().then((r) => setVatCategories(r.vatCategories)).catch(() => setVatCategories([]))
  }, [])

  const set = (key: Field, value: string) => {
    setDraft((d) => {
      if (d === null) return d
      const next = { ...d, [key]: value }
      // Not registered carries no VAT detail -- the database refuses the
      // combination. Clearing them HERE, visibly, is the page doing what the
      // person asked; the server never clears them on its own.
      if (key === 'vatRegistered' && value === 'false') {
        next.vatNumber = ''
        next.vatCategoryCode = ''
        next.vatRegisteredFrom = ''
      }
      return next
    })
    setSavedAt(null)
  }

  const patch = saved !== null && draft !== null ? changedFields(saved, draft) : {}
  const dirtyCount = Object.keys(patch).length
  const needsReason =
    saved !== null &&
    FIELDS.some((f) => f in patch && SENSITIVE.has(f) && saved[f] !== '')

  // Duplicate check on the two numbers, when either is being changed.
  const regDraft = draft?.registrationNumber ?? ''
  const vatDraft = draft?.vatNumber ?? ''
  const regChanged = saved !== null && regDraft !== saved.registrationNumber
  const vatChanged = saved !== null && vatDraft !== saved.vatNumber
  useEffect(() => {
    if (!regChanged && !vatChanged) {
      setDuplicates([])
      return
    }
    const timer = window.setTimeout(() => {
      api
        .duplicates({
          registrationNumber: regChanged ? regDraft : null,
          vatNumber: vatChanged ? vatDraft : null,
          excludeId: id,
        })
        .then((r) => setDuplicates(r.matches))
        .catch(() => setDuplicates([]))
    }, 400)
    return () => window.clearTimeout(timer)
  }, [regDraft, vatDraft, regChanged, vatChanged, id])

  // Unsaved changes survive neither a closed tab nor a stray click without a
  // question first (EQCO-MODE-003).
  useEffect(() => {
    if (dirtyCount === 0) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirtyCount])
  const leave = (e: React.MouseEvent) => {
    if (dirtyCount > 0 && !window.confirm(`Leave without saving ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}?`)) {
      e.preventDefault()
    }
  }

  const save = async () => {
    if (dirtyCount === 0) return
    setSaving(true)
    setSaveError(null)
    try {
      // The server returns the whole client, so anything it normalised or
      // filled in is on screen immediately rather than at the next reload.
      adopt(await api.updateClient(id, patch, needsReason ? reason : null))
      setReason('')
      setSavedAt(new Date().toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }))
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  // T&B-1 PR 2: the Log time dialog and what it last logged.
  const [loggingTime, setLoggingTime] = useState(false)
  const [timeLogged, setTimeLogged] = useState<string | null>(null)

  if (loadError !== null) {
    return (
      <AppShell obligationTypes={obligationTypes} auth={auth} eyebrow="Clients" title="Client">
        <ErrorState message={loadError} />
        <p className="mt-3 text-[12.5px]">
          <Link to="/clients" className="text-eq-navy underline">
            Back to the client register
          </Link>
        </p>
      </AppShell>
    )
  }

  if (client === null || draft === null) {
    return (
      <AppShell obligationTypes={obligationTypes} auth={auth} eyebrow="Clients" title="Client">
        <LoadingState what="this client" />
      </AppShell>
    )
  }

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Clients"
      title={client.shortName}
      subtitle={
        <>
          Client {client.clientNumber}
          {client.group !== null && <> · {client.group.name}</>}
          {' · '}
          {client._count.obligations} obligation{client._count.obligations === 1 ? '' : 's'},{' '}
          {client._count.jobs} job{client._count.jobs === 1 ? '' : 's'},{' '}
          {client._count.notes} note{client._count.notes === 1 ? '' : 's'}
        </>
      }
      action={
        <div className="flex items-center gap-3">
          {/* T&B-1 PR 2: log time against this client without leaving the page. */}
          {auth.mode === 'cognito' && (
            <button
              type="button"
              onClick={() => setLoggingTime(true)}
              className="rounded-eq-sm border border-eq-navy px-3 py-1.5 font-heading text-[12.5px] font-bold text-eq-navy"
            >
              Log time
            </button>
          )}
          {loggingTime && (
            <LogTimeDialog
              client={{ id: client.id, clientNumber: client.clientNumber, shortName: client.shortName }}
              onClose={() => setLoggingTime(false)}
              onSaved={(e) => {
                setLoggingTime(false)
                setTimeLogged(`Logged ${e.hours} h on ${e.job.name} as a draft on your week.`)
                setTimeVersion((v) => v + 1)
              }}
            />
          )}
          {timeLogged !== null && <span className="text-[12.5px] text-eq-helper">{timeLogged}</span>}
          {savedAt !== null && dirtyCount === 0 && (
            <span className="text-[12.5px] text-eq-helper">Saved at {savedAt}</span>
          )}
          {dirtyCount > 0 && (
            <button
              type="button"
              onClick={() => setDraft(saved)}
              className="text-[12.5px] text-eq-helper underline"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={dirtyCount === 0 || saving || (needsReason && reason.trim() === '')}
            className={[
              'rounded-eq-sm px-4 py-2 font-heading text-[13px] font-bold',
              dirtyCount === 0 || saving || (needsReason && reason.trim() === '')
                ? 'cursor-not-allowed bg-eq-grey text-eq-helper'
                : 'bg-eq-navy text-white',
            ].join(' ')}
          >
            {saving
              ? 'Saving…'
              : dirtyCount === 0
                ? 'No changes'
                : `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`}
          </button>
        </div>
      }
    >
      <p className="mb-4 text-[12.5px]">
        <Link to="/clients" onClick={leave} className="text-eq-navy underline">
          ← All clients
        </Link>
      </p>

      {needsReason && (
        <label className="mb-4 block rounded-eq-md border border-eq-warning bg-eq-warning-bg px-4 py-3">
          <span className="mb-1 block text-[14px] font-semibold text-eq-warning">
            Reason for this change *
          </span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. SARS notice of change, CIPC certificate"
            className={INPUT}
          />
          <span className="mt-1 block text-[12px] text-eq-neutral">
            You are changing a registration, VAT, year-end, entity type or status value that was
            already recorded. The reason is kept in the change history.
          </span>
        </label>
      )}

      {duplicates.length > 0 && (
        <div role="alert" className="mb-4 rounded-eq-md border border-eq-warning bg-eq-warning-bg px-4 py-3 text-[14px]">
          <div className="font-semibold text-eq-warning">Another client has the same {duplicates.some((d) => d.matchedOn.includes('vatNumber')) ? vatTerm('VAT_TERM_VAT_REGISTRATION_NUMBER') : 'registration number'}</div>
          <ul className="mt-1 list-none p-0">
            {duplicates.map((d) => (
              <li key={d.id}>
                <Link to={`/clients/${d.id}`} className="font-semibold text-eq-navy underline">
                  {d.clientNumber} · {d.shortName}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[12.5px] text-eq-neutral">
            Related entities can share these legitimately. Check before saving.
          </div>
        </div>
      )}

      {saveError !== null && (
        <div className="mb-4">
          <ErrorState message={saveError} />
        </div>
      )}

      {/* T&B-1 PR 5: the Time tab (spec, "Client time"). The details stay
          mounted while it shows, so unsaved edits are not lost by a look at
          the client's time. */}
      <div className="mb-4 flex gap-1 border-b border-eq-border" role="tablist">
        {(['details', 'time'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setParams(t === 'details' ? {} : { tab: t }, { replace: true })}
            className={[
              '-mb-px border-b-2 px-3 py-2 font-heading text-[13px] font-bold',
              tab === t ? 'border-eq-navy text-eq-navy' : 'border-transparent text-eq-neutral hover:text-eq-navy',
            ].join(' ')}
          >
            {t === 'details' ? 'Details' : 'Time'}
          </button>
        ))}
      </div>
      {tab === 'time' && <ClientTimeTab clientId={client.id} auth={auth} version={timeVersion} />}

      <div className={tab === 'time' ? 'hidden' : 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]'}>
        <div className="flex flex-col gap-4">
          {/* The work first: on a practice-management screen, what the client
              carries and who does it is what people open the page for. */}
          <ClientWork client={client} auth={auth} dirty={dirtyCount > 0} onClientChanged={adopt} />

          <Section title="Identity">
            <Field label="Short name" hint="How this client is listed everywhere in the suite.">
              <Text value={draft.shortName} onChange={(v) => set('shortName', v)} />
            </Field>
            <Field label="Legal name" hint="As registered at CIPC. Printed on tax invoices and formal reports.">
              <Text value={draft.registeredName} onChange={(v) => set('registeredName', v)} />
            </Field>
            <Field label="Trading name">
              <Text value={draft.tradingName} onChange={(v) => set('tradingName', v)} />
            </Field>
            <Field
              label="Entity type"
              hint={
                client.entityTypeSourceHint === null
                  ? undefined
                  : `SmartPractice had this as "${client.entityTypeSourceHint}". The two do not map cleanly, so it has to be set by hand.`
              }
            >
              <Select
                value={draft.entityType}
                onChange={(v) => set('entityType', v)}
                blank="Not set"
                options={ENTITY_TYPES.map(([v, l]) => [v, l])}
              />
            </Field>
            <Field label="Registration number" hint="CIPC or trust number. Kept exactly as typed. An individual's ID number has its own field.">
              <Text value={draft.registrationNumber} onChange={(v) => set('registrationNumber', v)} />
            </Field>
            <Field
              label="SA ID number"
              hint={
                client?.idNumberBirthDate
                  ? `Born ${client.idNumberBirthDate} (age ${client.idNumberAge}), read from the saved number.`
                  : 'Individuals only. 13 digits; checked before it is saved.'
              }
            >
              <Text value={draft.idNumber} onChange={(v) => set('idNumber', v)} />
            </Field>
            <Field label="Status">
              <Select
                value={draft.status}
                onChange={(v) => set('status', v)}
                options={STATUSES.map(([v, l]) => [v, l])}
              />
            </Field>
            <Field label="Group" hint="Families and related entities that are billed or worked together.">
              <Select
                value={draft.groupId}
                onChange={(v) => set('groupId', v)}
                blank="No group"
                options={groups.map((g) => [
                  g.id,
                  g.groupNumber === null
                    ? `${g.name} (${g._count.clients})`
                    : `${g.groupNumber} — ${g.name} (${g._count.clients})`,
                ])}
              />
            </Field>
            <Field label="Shelf number" hint="Physical file location. No business meaning; reused freely.">
              <Text value={draft.shelfNumber} onChange={(v) => set('shelfNumber', v)} />
            </Field>
          </Section>

          <Section title="Statutory registrations">
            <Field label="Income tax number">
              <Text value={draft.incomeTaxNumber} onChange={(v) => set('incomeTaxNumber', v)} />
            </Field>
            <Field label="PAYE number">
              <Text value={draft.payeNumber} onChange={(v) => set('payeNumber', v)} />
            </Field>
            <Field label="UIF number" hint="Department of Labour registration.">
              <Text value={draft.uifNumber} onChange={(v) => set('uifNumber', v)} />
            </Field>
            <Field label="UIF payment reference" hint="The SARS reference on the EMP201, e.g. U200745208. Not the UIF number.">
              <Text value={draft.uifRefSars} onChange={(v) => set('uifRefSars', v)} />
            </Field>
            <Field label="SDL number">
              <Text value={draft.sdlNumber} onChange={(v) => set('sdlNumber', v)} />
            </Field>
            <Field label="COIDA / WCC number" hint="Compensation Fund. Not the same as the UIF number.">
              <Text value={draft.coidaNumber} onChange={(v) => set('coidaNumber', v)} />
            </Field>
            <Field label="SIC code" hint="Standard Industrial Classification.">
              <Text value={draft.sicCode} onChange={(v) => set('sicCode', v)} />
            </Field>
            <Field label="Trade classification" hint="Department of Labour / SETA code, e.g. 0355.">
              <Text value={draft.tradeClassification} onChange={(v) => set('tradeClassification', v)} />
            </Field>
          </Section>

          <Section title="Financial year">
            <Field
              label="Financial year-end month"
              hint="The LAST month of the financial year. Periods cannot be generated for this client until it is set."
            >
              <Select
                value={draft.financialYearEndMonth}
                onChange={(v) => set('financialYearEndMonth', v)}
                blank="Not known"
                options={MONTHS.map((m, i) => [String(i + 1), m])}
              />
            </Field>
          </Section>

          <Section title="VAT">
            <Field
              label={vatTerm('VAT_TERM_VAT_REGISTRATION_STATUS')}
              hint={
                draft.vatRegistered === ''
                  ? 'Not captured yet. This is not the same as "not registered" -- Accounts cannot be set up until it is answered.'
                  : draft.vatRegistered === 'false'
                    ? 'A client that is Not Registered for VAT carries no VAT Registration Number, VAT Tax Period Frequency or VAT Registration Date.'
                    : undefined
              }
            >
              <Select
                value={draft.vatRegistered}
                onChange={(v) => set('vatRegistered', v)}
                blank="Not captured"
                options={[
                  ['true', vatTerm('VAT_TERM_REGISTERED_FOR_VAT')],
                  ['false', vatTerm('VAT_TERM_NOT_REGISTERED_FOR_VAT')],
                ]}
              />
            </Field>
            {draft.vatRegistered !== 'false' && (
              <>
                <Field
                  label={draft.vatRegistered === 'true' ? `${vatTerm('VAT_TERM_VAT_REGISTRATION_NUMBER')} *` : vatTerm('VAT_TERM_VAT_REGISTRATION_NUMBER')}
                  hint="Printed on every tax invoice and credit note."
                >
                  <Text value={draft.vatNumber} onChange={(v) => set('vatNumber', v)} />
                </Field>
                <Field label={vatTerm('VAT_TERM_VAT_REGISTRATION_DATE')} hint="No VAT Tax Period starts before this date.">
                  <input
                    type="date"
                    value={draft.vatRegisteredFrom}
                    onChange={(e) => set('vatRegisteredFrom', e.target.value)}
                    className={INPUT}
                  />
                </Field>
                <Field
                  label={`${vatTerm('VAT_TERM_VAT_TAX_PERIOD_FREQUENCY')} (category)`}
                  hint={
                    draft.vatCategoryCode === 'A'
                      ? 'Bi-monthly cycle 1: periods end Jan, Mar, May, Jul, Sep, Nov.'
                      : draft.vatCategoryCode === 'B'
                        ? 'Bi-monthly cycle 2: periods end Feb, Apr, Jun, Aug, Oct, Dec.'
                        : 'The category decides the VAT Tax Periods and VAT201 due dates. The bi-monthly cycle follows from it.'
                  }
                >
                  <Select
                    value={draft.vatCategoryCode}
                    onChange={(v) => set('vatCategoryCode', v)}
                    blank="Not set"
                    options={vatCategories.map((c) => [
                      c.code,
                      c.filingFrequency === null ? `${c.code}` : `${c.code} — ${c.filingFrequency}`,
                    ])}
                  />
                </Field>
              </>
            )}
          </Section>

          <Section title="Registered address">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Unit">
                <Text value={draft.physUnit} onChange={(v) => set('physUnit', v)} />
              </Field>
              <Field label="Complex or building">
                <Text value={draft.physComplex} onChange={(v) => set('physComplex', v)} />
              </Field>
              <Field label="Street number">
                <Text value={draft.physStreetNo} onChange={(v) => set('physStreetNo', v)} />
              </Field>
              <Field label="Street name">
                <Text value={draft.physStreetName} onChange={(v) => set('physStreetName', v)} />
              </Field>
              <Field label="Suburb">
                <Text value={draft.physSuburb} onChange={(v) => set('physSuburb', v)} />
              </Field>
              <Field label="City or town">
                <Text value={draft.physCity} onChange={(v) => set('physCity', v)} />
              </Field>
              <Field label="Postal code">
                <Text value={draft.physPostalCode} onChange={(v) => set('physPostalCode', v)} />
              </Field>
              <Field label="Country">
                <Select
                  value={draft.physCountry}
                  onChange={(v) => set('physCountry', v)}
                  blank="Not set"
                  options={[
                    ['South Africa', 'South Africa'],
                    ...(draft.physCountry !== '' && draft.physCountry !== 'South Africa'
                      ? ([[draft.physCountry, draft.physCountry]] as const)
                      : []),
                  ]}
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-[14px]">
              <input
                type="checkbox"
                checked={draft.postalSameAsPhysical === 'true'}
                onChange={(e) => set('postalSameAsPhysical', String(e.target.checked))}
                className="h-4 w-4"
              />
              Postal address is the same as the registered address
            </label>
            {draft.postalSameAsPhysical !== 'true' && (
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Postal line 1">
                  <Text value={draft.postalLine1} onChange={(v) => set('postalLine1', v)} />
                </Field>
                <Field label="Postal line 2">
                  <Text value={draft.postalLine2} onChange={(v) => set('postalLine2', v)} />
                </Field>
                <Field label="Postal line 3">
                  <Text value={draft.postalLine3} onChange={(v) => set('postalLine3', v)} />
                </Field>
                <Field label="Postal code">
                  <Text value={draft.postalPostalCode} onChange={(v) => set('postalPostalCode', v)} />
                </Field>
              </div>
            )}
          </Section>

          <Section title="Change history">
            {changes.length === 0 ? (
              <p className="text-[12.5px] text-eq-helper">
                No changes recorded yet. The history starts with the first save in this version of Practice.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="border-b border-eq-border text-left text-eq-neutral">
                      <th className="px-2 py-1 font-semibold">When</th>
                      <th className="px-2 py-1 font-semibold">Who</th>
                      <th className="px-2 py-1 font-semibold">What</th>
                      <th className="px-2 py-1 font-semibold">Was</th>
                      <th className="px-2 py-1 font-semibold">Now</th>
                      <th className="px-2 py-1 font-semibold">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((c) => (
                      <tr key={c.id} className="border-b border-eq-border last:border-0 align-top">
                        <td className="whitespace-nowrap px-2 py-1 tabular-nums">
                          {new Date(c.changedAt).toLocaleString('en-ZA', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                          })}
                        </td>
                        <td className="px-2 py-1">{c.changedBy ?? '—'}</td>
                        <td className="px-2 py-1">{FIELD_WORDS[c.field] ?? c.field}</td>
                        <td className="px-2 py-1 text-eq-neutral">{historyValue(c.field, c.oldValue)}</td>
                        <td className="px-2 py-1">{historyValue(c.field, c.newValue)}</td>
                        <td className="px-2 py-1 text-eq-neutral">{c.reason ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        <div className="flex flex-col gap-4">
          <Section title="Fixed">
            <ReadOnly
              label="Client number"
              value={String(client.clientNumber)}
              hint="Assigned once at onboarding and never reused. The client's Accounts ledger is named after it, so it cannot be changed here or anywhere else — the database refuses it too."
            />
            <ReadOnly
              label="SmartPractice customer id"
              value={client.smartpracticeCustomerId}
              hint="How the SmartPractice import recognises this client on a re-run, and how the register is reconciled back to the source export."
            />
            <ReadOnly
              label="Onboarded"
              value={client.dateOnboarded === null ? null : client.dateOnboarded.slice(0, 10)}
              hint="Blank means the real onboarding date is genuinely unknown, not that it is missing."
            />
          </Section>

          <Section title="Products for this client">
            {readinessError !== null && <ErrorState message={readinessError} />}
            {readiness === null && readinessError === null && (
              <p className="text-[12.5px] text-eq-helper">Checking…</p>
            )}
            {readiness?.map((p) => (
              <div key={p.product} className="rounded-eq-sm border border-eq-border px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="font-heading text-[14px] font-bold">{PRODUCT_NAMES[p.product] ?? p.product}</span>
                  <span
                    className={[
                      'rounded-eq-pill px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.06em]',
                      p.ready ? 'bg-eq-success-bg text-eq-success' : 'bg-eq-neutral-bg text-eq-neutral',
                    ].join(' ')}
                  >
                    {p.ready ? 'Details complete' : `${p.missing.length} missing`}
                  </span>
                </div>
                {p.missing.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-[12.5px]">
                    {p.missing.map((m) => (
                      <li key={m.field} title={m.rule}>
                        {m.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <p className="text-[12px] text-eq-helper">
              What each product needs before this client can be set up in it. The rules are the
              practice's requirement table; hover an item for why it is required.
            </p>
          </Section>

          <Section title="Contacts">
            {client.contacts.length === 0 ? (
              <p className="text-[12.5px] text-eq-helper">No contacts recorded.</p>
            ) : (
              <ul className="flex list-none flex-col gap-2 p-0">
                {client.contacts.map((c) => (
                  <li key={c.id} className="text-[14px]">
                    <span className="font-semibold">{c.contact.fullName}</span>
                    {c.isPrimary && (
                      <span className="ml-2 rounded-eq-pill bg-eq-grey px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.06em] text-eq-neutral">
                        Primary
                      </span>
                    )}
                    {c.role !== null && (
                      <div className="text-[12.5px] text-eq-helper">{c.role}</div>
                    )}
                    {c.contact.email !== null && (
                      <div className="text-[12.5px] text-eq-helper">{c.contact.email}</div>
                    )}
                    {c.contact.phone !== null && (
                      <div className="text-[12.5px] text-eq-helper">{c.contact.phone}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Remove ${c.contact.fullName} from this client? The person stays on any other client.`)) {
                          api.removeContact(id, c.id).then(adopt).catch((e: unknown) => setSaveError(e instanceof Error ? e.message : String(e)))
                        }
                      }}
                      className="text-[12px] text-eq-danger underline"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <AddContact
              onAdd={async (body) => {
                adopt(await api.addContact(id, body))
              }}
            />
          </Section>
        </div>
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-eq-md border border-eq-border bg-white px-5 py-4">
      <h2 className="mb-3 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral">
        {title}
      </h2>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12.5px] font-semibold text-eq-neutral">{label}</span>
      {children}
      {hint !== undefined && <span className="mt-1 block text-[12px] text-eq-helper">{hint}</span>}
    </label>
  )
}

function ReadOnly({
  label,
  value,
  hint,
}: {
  label: string
  value: string | null
  hint: string
}) {
  return (
    <div>
      <div className="text-[12.5px] font-semibold text-eq-neutral">{label}</div>
      <div className="text-[16px] tabular-nums">
        {value === null || value === '' ? <span className="text-eq-helper">—</span> : value}
      </div>
      <div className="mt-1 text-[12px] text-eq-helper">{hint}</div>
    </div>
  )
}

const INPUT =
  'w-full rounded-eq-sm border border-eq-border bg-white px-3 py-2 text-[14px] focus:border-eq-navy focus:outline-none'

function Text({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className={INPUT} />
}

function Select({
  value,
  onChange,
  options,
  blank,
}: {
  value: string
  onChange: (v: string) => void
  options: readonly (readonly [string, string])[]
  /** The label for "no value". Omitted where the field cannot be empty. */
  blank?: string
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={INPUT}>
      {blank !== undefined && <option value="">{blank}</option>}
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )
}

/**
 * Link a person to this client. Saves on its own, immediately -- a contact is
 * a separate record from the client's fields, and a shared Save button would
 * leave the person guessing which half failed.
 */
function AddContact({
  onAdd,
}: {
  onAdd: (body: { fullName: string; email: string | null; phone: string | null; role: string | null; isPrimary: boolean }) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="self-start text-[13px] font-semibold text-eq-navy underline">
        Add contact
      </button>
    )
  }
  const blank = (v: string) => (v.trim() === '' ? null : v.trim())
  return (
    <div className="flex flex-col gap-2 rounded-eq-sm border border-eq-border p-3">
      <Field label="Name *">
        <Text value={fullName} onChange={setFullName} />
      </Field>
      <Field label="Role" hint="e.g. Main contact, SARS representative, Receives invoices">
        <Text value={role} onChange={setRole} />
      </Field>
      <Field label="E-mail">
        <Text value={email} onChange={setEmail} />
      </Field>
      <Field label="Telephone">
        <Text value={phone} onChange={setPhone} />
      </Field>
      <label className="flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} className="h-4 w-4" />
        Main contact for this client
      </label>
      {error !== null && <ErrorState message={error} />}
      <div className="flex gap-3">
        <button
          type="button"
          disabled={busy || fullName.trim() === ''}
          onClick={() => {
            setBusy(true)
            setError(null)
            onAdd({ fullName: fullName.trim(), email: blank(email), phone: blank(phone), role: blank(role), isPrimary })
              .then(() => {
                setOpen(false)
                setFullName(''); setEmail(''); setPhone(''); setRole(''); setIsPrimary(false)
              })
              .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false))
          }}
          className="rounded-eq-sm border border-eq-navy px-3 py-1.5 font-heading text-[12.5px] font-bold text-eq-navy disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-[12.5px] text-eq-helper underline">
          Cancel
        </button>
      </div>
    </div>
  )
}
